import React, { useState, useEffect } from "react";
import styled from "styled-components";
import { useCart } from "../context/CartContext";
import { useLocation, useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTimes, faPlus, faMinus } from "@fortawesome/free-solid-svg-icons";
import BottomMenu from "../components/BottomMenu";

export default function CartScreen() {
    const { cartItems, setCartItems } = useCart();
    const navigate = useNavigate();
    const location = useLocation();
    const selectedFridgeId = location.state?.selectedFridgeId ?? localStorage.getItem("selectedFridgeId") ?? null;
    const [fridgeName, setFridgeName] = useState("");

    // Scrolla till toppen när komponenten laddas
    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    useEffect(() => {
        const fetchFridgeDetails = async () => {
            if (!selectedFridgeId) return;

            try {
                const response = await fetch(`https://boxmealadminapinew.azurewebsites.net/api/Fridge/${selectedFridgeId}`);
                if (!response.ok) throw new Error("Failed to fetch fridge details");

                const data = await response.json();
                setFridgeName(data.fridgeName || "Unknown Fridge");
            } catch (error) {
                console.error("Error fetching fridge details:", error);
                setFridgeName("Dospace Drottningen"); // Fallback name
            }
        };

        fetchFridgeDetails();
    }, [selectedFridgeId]);

    const handleRemove = (itemId) => {
        const updatedCartItems = cartItems.filter((item) => item.id !== itemId);
        setCartItems(updatedCartItems);
    };

    const updateQuantity = (itemId, newQuantity) => {
        if (newQuantity <= 0) {
            handleRemove(itemId);
            return;
        }

        const updatedCartItems = cartItems.map((item) =>
            item.id === itemId ? { ...item, quantity: newQuantity } : item
        );
        setCartItems(updatedCartItems);
    };

    const calculateTotal = () => {
        return cartItems
            .reduce((acc, item) => acc + item.price * item.quantity, 0)
            .toFixed(0);
    };

    const calculateItemTotal = (price, quantity) => {
        return (price * quantity).toFixed(0);
    };

    if (cartItems.length === 0) {
        return (
            <Container>
                <EmptyContainer>
                    <EmptyTitle>Din varukorg är tom</EmptyTitle>
                    <EmptyText>Lägg till produkter för att börja handla</EmptyText>
                    <BackToProductsButton onClick={() => navigate("/products")}>
                        Tillbaka till produkter
                    </BackToProductsButton>
                </EmptyContainer>

                <BottomMenuSpacer />
                <BottomMenu selectedFridgeId={selectedFridgeId} />
            </Container>
        );
    }

    return (
        <Container>
            <ContentWrapper>
                <ItemsList>
                    {cartItems.map((item) => (
                        <CartItemCard key={item.id}>
                            <CardHeader>
                                <RemoveButton onClick={() => handleRemove(item.id)}>
                                    <FontAwesomeIcon icon={faTimes} />
                                </RemoveButton>
                                <ItemTotal>{calculateItemTotal(item.price, item.quantity)}kr</ItemTotal>
                            </CardHeader>

                            <CardContent>
                                <ProductImageContainer>
                                    <ProductImage src={item.imageURL} alt={item.title} />
                                </ProductImageContainer>

                                <ProductInfo>
                                    <ProductTitle>{item.title}</ProductTitle>
                                    {item.quantity > 1 && (
                                        <PriceCalculation>
                                            {item.quantity} × {item.price}kr
                                        </PriceCalculation>
                                    )}
                                </ProductInfo>

                                <QuantitySection>
                                    <QuantityControls>
                                        <QuantityButton
                                            onClick={() => updateQuantity(item.id, item.quantity - 1)}
                                            disabled={item.quantity <= 1}
                                            $variant="decrease"
                                        >
                                            <FontAwesomeIcon icon={faMinus} />
                                        </QuantityButton>
                                        <QuantityDisplay>{item.quantity}</QuantityDisplay>
                                        <QuantityButton
                                            onClick={() => updateQuantity(item.id, item.quantity + 1)}
                                            $variant="increase"
                                        >
                                            <FontAwesomeIcon icon={faPlus} />
                                        </QuantityButton>
                                    </QuantityControls>
                                </QuantitySection>
                            </CardContent>
                        </CartItemCard>
                    ))}
                </ItemsList>

                <CheckoutSection>
                    <FridgeInfo>
                        <FridgeLabel>Du handlar från:</FridgeLabel>
                        <FridgeNameText>{fridgeName}</FridgeNameText>
                    </FridgeInfo>

                    <Divider />

                    <TotalSection>
                        <TotalLabel>Totalt</TotalLabel>
                        <TotalAmount>{calculateTotal()}kr</TotalAmount>
                    </TotalSection>

                    <CheckoutButton
                        onClick={() =>
                            navigate("/choose-payment", { state: { selectedFridgeId } })
                        }
                    >
                        Betala
                    </CheckoutButton>
                </CheckoutSection>
            </ContentWrapper>

            <BottomMenuSpacer />
            <BottomMenu selectedFridgeId={selectedFridgeId} />
        </Container>
    );
}

/* ===== Styles ===== */
const Container = styled.div`
    height: 100dvh; /* was 100vh; use dynamic viewport on mobile */
    min-height: 100vh; /* fallback */
    background: #f6f7fa;
    display: flex;
    flex-direction: column;
    overflow-x: hidden; /* avoid vertical clipping; was overflow: hidden */
`;

const ContentWrapper = styled.div`
    flex: 1;
    padding: 20px;
    display: flex;
    flex-direction: column;
    padding-top: 40px;
    overflow: hidden;
`;

const ItemsList = styled.div`
    flex: 1;
    margin-bottom: 200px;
    padding-bottom: 20px;
    padding-top: 12px; /* add breathing room so the first card isn't cut at the top */
    overflow-y: auto;

    /* Smooth scrolling */
    scroll-behavior: smooth;

    /* Custom scrollbar */
    &::-webkit-scrollbar {
        width: 6px;
    }

    &::-webkit-scrollbar-track {
        background: #f1f1f1;
        border-radius: 3px;
    }

    &::-webkit-scrollbar-thumb {
        background: #ccc;
        border-radius: 3px;

        &:hover {
            background: #999;
        }
    }

    -webkit-overflow-scrolling: touch; /* iOS momentum scroll */
    overscroll-behavior-y: contain; /* prevent parent/body scroll chaining */
`;

const CartItemCard = styled.div`
    background: rgba(255, 255, 255, 0.9);
    backdrop-filter: blur(12px);
    border: 1px solid rgba(255, 255, 255, 0.9);
    border-radius: 16px;
    padding: 0;
    margin-bottom: 12px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
    overflow: hidden;
    transition: all 0.3s ease;

    &:hover {
        transform: translateY(-1px);
        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
    }
`;

const CardHeader = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 10px 16px 6px 16px;
    background: rgba(246, 247, 250, 0.5);
    border-bottom: 1px solid rgba(229, 231, 235, 0.3);
`;

const RemoveButton = styled.button`
    width: 24px;
    height: 24px;
    border-radius: 50%;
    border: none;
    background: rgba(37, 37, 37, 0.9);
    color: white;
    font-size: 10px;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    transition: all 0.3s ease;

    &:hover {
        background: rgba(37, 37, 37, 1);
        transform: scale(1.1);
    }
`;

const ItemTotal = styled.div`
    font-size: 16px;
    font-weight: 700;
    color: #1f2937;
    background: rgba(255, 255, 255, 0.8);
    padding: 4px 12px;
    border-radius: 12px;
    border: 1px solid rgba(229, 231, 235, 0.4);
`;

const CardContent = styled.div`
    padding: 12px 16px;
    display: flex;
    align-items: center;
    gap: 12px;
`;

const ProductImageContainer = styled.div`
    position: relative;
    flex-shrink: 0;
`;

const ProductImage = styled.img`
    width: 60px;
    height: 60px;
    object-fit: contain;
    border-radius: 12px;
    background: rgba(249, 249, 249, 0.8);
    padding: 6px;
    border: 1px solid rgba(229, 231, 235, 0.3);
`;

const ProductInfo = styled.div`
    flex: 1;
    min-width: 0;
`;

const ProductTitle = styled.h3`
    font-size: 15px;
    font-weight: 600;
    color: #1f2937;
    margin: 0 0 6px 0;
    line-height: 1.3;
    overflow: hidden;
    text-overflow: ellipsis;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
`;

const PriceCalculation = styled.div`
    font-size: 12px;
    color: #6b7280;
    font-style: italic;
    margin-top: 2px;
`;

const QuantitySection = styled.div`
    flex-shrink: 0;
`;

const QuantityControls = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
    background: rgba(246, 247, 250, 0.8);
    border: 1px solid rgba(229, 231, 235, 0.4);
    border-radius: 12px;
    padding: 4px;
`;

const QuantityButton = styled.button`
    width: 28px;
    height: 28px;
    border-radius: 8px;
    background: ${({ $variant, disabled }) => {
        if (disabled) return 'rgba(156, 163, 175, 0.5)';
        return $variant === 'increase' ? 'rgba(37, 37, 37, 0.9)' : 'rgba(37, 37, 37, 0.9)';
    }};
    color: ${({ disabled }) => disabled ? 'rgba(255, 255, 255, 0.6)' : 'white'};
    border: none;
    font-size: 10px;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: ${({ disabled }) => disabled ? 'not-allowed' : 'pointer'};
    transition: all 0.3s ease;

    &:hover:not(:disabled) {
        background: rgba(37, 37, 37, 1);
        transform: scale(1.05);
    }

    &:active:not(:disabled) {
        transform: scale(0.95);
    }
`;

const QuantityDisplay = styled.div`
    background: rgba(255, 255, 255, 0.9);
    border: 1px solid rgba(229, 231, 235, 0.4);
    border-radius: 8px;
    padding: 6px 10px;
    font-size: 13px;
    font-weight: 600;
    color: #1f2937;
    min-width: 36px;
    text-align: center;
`;

const CheckoutSection = styled.div`
    position: fixed;
    bottom: 0;
    left: 0;
    right: 0;
    background: rgba(255, 255, 255, 0.95);
    backdrop-filter: blur(16px);
    border-top: 1px solid rgba(255, 255, 255, 0.9);
    border-radius: 25px 25px 0 0;
    padding: 20px 20px 100px 20px;
    box-shadow: 0 -4px 20px rgba(0, 0, 0, 0.1);
    z-index: 999;
`;

const FridgeInfo = styled.div`
    text-align: center;
    margin-bottom: 16px;
`;

const FridgeLabel = styled.div`
    font-size: 14px;
    color: #6b7280;
    margin-bottom: 4px;
`;

const FridgeNameText = styled.div`
    font-size: 16px;
    font-weight: 600;
    color: #1f2937;
`;

const Divider = styled.hr`
    border: none;
    height: 1px;
    background: #e5e7eb;
    margin: 16px 0;
`;

const TotalSection = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 20px;
    gap: 8px;
`;

const TotalLabel = styled.div`
    font-size: 20px;
    font-weight: 600;
    color: #1f2937;
`;

const TotalAmount = styled.div`
    font-size: 20px;
    font-weight: 700;
    color: #1f2937;
`;

const CheckoutButton = styled.button`
    width: 100%;
    background: rgba(37, 37, 37, 0.9);
    color: white;
    border: none;
    border-radius: 16px;
    padding: 16px;
    font-size: 16px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.3s ease;
    text-transform: uppercase;
    letter-spacing: 0.5px;

    &:hover {
        background: rgba(37, 37, 37, 1);
        transform: translateY(-1px);
        box-shadow: 0 4px 16px rgba(37, 37, 37, 0.3);
    }

    &:active {
        transform: translateY(0);
    }
`;

const EmptyContainer = styled.div`
    flex: 1;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 40px 20px;
    text-align: center;
`;

const EmptyTitle = styled.h2`
    font-size: 24px;
    font-weight: 600;
    color: #1f2937;
    margin: 0 0 8px 0;
`;

const EmptyText = styled.p`
    font-size: 16px;
    color: #6b7280;
    margin: 0 0 32px 0;
`;

const BackToProductsButton = styled.button`
    background: rgba(37, 37, 37, 0.9);
    color: white;
    border: none;
    border-radius: 16px;
    padding: 14px 28px;
    font-size: 14px;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.3s ease;

    &:hover {
        background: rgba(37, 37, 37, 1);
        transform: translateY(-1px);
    }
`;

const BottomMenuSpacer = styled.div`
    height: 80px;
    flex-shrink: 0;
`;